/**
 * Catálogo de permissões da Home do protótipo (`MENU_ITEMS` em `menuHome.constants.js`).
 *
 * Fonte de verdade dos códigos: `src/routes/menuRoutes.jsx` (catálogo real usado
 * por `SideDrawerNavigation.jsx`/`hasNotUserPermissions`). Cada `gate`/entrada de
 * `viewPermissions` abaixo foi validada linha a linha contra esse arquivo — não
 * inventar/alterar um código sem repetir essa validação, um código errado libera
 * ou bloqueia a tela errada silenciosamente.
 *
 * Consumido pela Etapa 3 (RBAC real em `HomePage.jsx`/`HomeMenuButton.jsx`) via
 * `hasUserPermissions(...codigos)`.
 *
 * @typedef {Object} MenuHomePermission
 * @property {number[] | null} gate - Permissão do botão/grupo. `null` = sempre
 *   habilitado, sem checagem.
 * @property {Record<string, number[] | null> | null} viewPermissions - Permissão
 *   de cada opção do dropdown, indexada pelo texto exibido (mesma string usada em
 *   `MENU_ITEMS[].views`). `null` = item sem `views` (kind 'action'/'search').
 *   Por opção, `null` = sempre visível.
 */

/**
 * Sentinela para opções sem permissão real correspondente em `menuRoutes.jsx`
 * (decisão de produto registrada em 2026-08-20 no plano macro). Diferente de
 * `null` (sempre visível): representa "nunca visível até existir permissão real".
 * Funciona porque `hasUserPermissions()` chamada sem argumentos já retorna
 * `false` (ver `src/helpers/utils/permissoes.js`) — o mesmo mecanismo de
 * checagem cobre os dois casos sem precisar de um terceiro estado no schema.
 */
export const NO_PERMISSION_AVAILABLE = Object.freeze([]);

/** @type {Record<string, MenuHomePermission>} */
export const MENU_HOME_PERMISSIONS = {
  localizar: { gate: null, viewPermissions: null },
  numeroProcesso: { gate: null, viewPermissions: null },

  consultaDinamica: { gate: [1301], viewPermissions: null },

  // Enable do grupo é dinâmico (derivado dos children visíveis) — Etapa 3
  // decide como calcular; aqui não há um código de grupo fixo.
  manutencao: {
    gate: [2601],
    viewPermissions: {
      'Alterar Cliente Processo': [2601],
      'Alterar Comercial Processo': [2601],
      'Alterar Percentual de Recebimento': [2601],
    },
  },

  cadastros: {
    gate: null,
    viewPermissions: {
      Parâmetros: [601, 602],
      Usuários: null,
      Filiais: null,
      'Ramos de Atividade': null,
      'Fases e Pastas': null,
      Requisições: null,
      'Texto Padrão': null,
      'Títulos de Compromisso': null,
    },
  },

  // Fixo desabilitado — decisão de produto 2026-08-20, não depende do usuário.
  minhasRequisicoes: {
    gate: NO_PERMISSION_AVAILABLE,
    viewPermissions: {
      'Requisições a serem atendidas': NO_PERMISSION_AVAILABLE,
      'Requisições solicitadas': NO_PERMISSION_AVAILABLE,
      'Requisições atendidas': NO_PERMISSION_AVAILABLE,
    },
  },

  // Gate = união apenas das próprias views do grupo (mesmo padrão de
  // `ferramentas`/`borderos`), não o código de grupo real de `menuRoutes.jsx`
  // (que inclui 1801-1803 de "C/C de Cliente" — view que na Home pertence ao
  // grupo `clientes`, não a este). Copiar o código real geraria um botão
  // habilitado com dropdown vazio para quem só tem 1801/1802/1803. Divergência
  // deliberada do valor literal da tabela do plano macro — revisada em code
  // review nesta sessão.
  financeiro: {
    gate: [1601, 1602, 1603, 1604, 1701, 1702, 1804, 1902, 1207],
    viewPermissions: {
      'C/C de Comissionado': [1601, 1602, 1603, 1604],
      'C/C de Honorários': [1701, 1702],
      'Notas Fiscais': [1804],
      'Notas Fiscais de Devedores': [1804],
      'Recebimento de Clientes': [1902],
      'Recebimento de Devedores': [1902],
      'Pagamento Direto': [1207],
    },
  },

  analiseGerenciais: {
    gate: [2401],
    viewPermissions: {
      'Rendimento Mensal Comercial': [2401],
      'Rendimento Mensal Negociadores': [2401],
      'Clientes em Débito': [2401],
      'Valores a Pagar pela ABE': [2401],
    },
  },

  estatisticaBorderos: {
    gate: [2301, 2302],
    viewPermissions: {
      'Controle de Entrada': [2301],
      'Mapa de Borderôs': [2301],
      'Resumo Mensal': [2302],
    },
  },

  clientes: {
    gate: null,
    viewPermissions: {
      'C/C de Cliente': [1801, 1802, 1803, 1804],
      'Dados do Cliente': null,
    },
  },

  ferramentas: {
    gate: [2101, 1212, 2303, 2305],
    viewPermissions: {
      'Registrar Boletos': [2101],
      'Validar Processos': [2305],
      'Consultar Estatísticas': [2303],
      'Gerar Arquivo WhatsApp': [1212],
    },
  },

  borderos: {
    gate: [801, 802, 803, 1210],
    viewPermissions: {
      Importar: [803],
      Incluir: [801],
      Processar: [802],
      'Visualizar Log': [802],
      'Validar Borderô': [803],
      'E-mail Entrada Borderô': [1210],
    },
  },
};
